import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { Luggage, MessageCircle } from "lucide-react";
import BottomTabNav from "../components/BottomTabNav";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_interpret-now/artifacts/8sijz41w_PassportTalk.png";

const HomePage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white p-6 pb-32">
      <div className="max-w-2xl mx-auto">
        {/* Header with Logo */}
        <div className="flex flex-col items-center mb-12 mt-8">
          <img 
            src={LOGO_URL} 
            alt="PassportTalk logo" 
            className="w-24 h-24 mb-6 object-contain"
          />
          <div className="flex items-center gap-2 mb-3">
            <h1 className="text-3xl sm:text-4xl font-bold text-travel-navy font-outfit text-center" data-testid="home-title">
                          What can I help with today?
            </h1>
          </div>
          <span className="px-3 py-1 bg-[#0EA5E9]/10 text-[#0EA5E9] text-xs font-semibold rounded-full mb-4">Beta</span>
          <p className="text-slate-600 text-center text-sm sm:text-base max-w-md">
            Choose how you'd like to communicate. AI helps first.
          </p>
        </div>

        {/* Two Primary Option Cards */}
        <div className="space-y-4">
          {/* Traveling Abroad Card */}
          <button
            onClick={() => navigate("/scenarios")}
            className="w-full bg-white border-2 border-slate-200 hover:border-[#0EA5E9] rounded-2xl p-6 text-left transition-all hover:shadow-lg hover:scale-[1.01] active:scale-[0.99] group"
            data-testid="travel-card"
          >
            <div className="flex items-start gap-5">
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#0EA5E9] to-[#06b6d4] flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-105 transition-transform">
                <Luggage className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold text-travel-navy font-outfit mb-2 flex items-center gap-2">
                  🧳 Traveling Abroad
                </h2>
                <p className="text-slate-600 text-sm mb-3">
                  Scenario-based translation for emergencies, food, hotel, directions & more.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-slate-100 text-slate-600 text-xs font-medium rounded-full">
                    Scenarios
                  </span>
                  <span className="px-3 py-1 bg-slate-100 text-slate-600 text-xs font-medium rounded-full">
                    Live Agents
                  </span>
                  <span className="px-3 py-1 bg-slate-100 text-slate-600 text-xs font-medium rounded-full">
                    Quick Phrases
                  </span>
                </div>
              </div>
            </div>
          </button>

          {/* Everyday Conversation Card */}
          <button
            onClick={() => navigate("/conversation")}
            className="w-full bg-white border-2 border-slate-200 hover:border-[#10b981] rounded-2xl p-6 text-left transition-all hover:shadow-lg hover:scale-[1.01] active:scale-[0.99] group"
            data-testid="conversation-card"
          >
            <div className="flex items-start gap-5">
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#10b981] to-[#059669] flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-105 transition-transform">
                <MessageCircle className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold text-travel-navy font-outfit mb-2 flex items-center gap-2">
                  💬 Everyday Conversation
                </h2>
                <p className="text-slate-600 text-sm mb-3">
                  Natural, dialect-aware messaging for WhatsApp and daily conversations.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-emerald-50 text-emerald-600 text-xs font-medium rounded-full">
                    WhatsApp Ready
                  </span>
                  <span className="px-3 py-1 bg-emerald-50 text-emerald-600 text-xs font-medium rounded-full">
                    Dialect Selection
                  </span>
                  <span className="px-3 py-1 bg-emerald-50 text-emerald-600 text-xs font-medium rounded-full">
                    Copy & Paste
                  </span>
                </div>
              </div>
            </div>
          </button>
        </div>

        {/* Bottom info with legal links */}
        <div className="mt-8 text-center space-y-3">
          <p className="text-xs text-slate-500">
            PassportTalk (Beta). Language assistance only. Not medical or legal advice.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2 text-xs">
            <Link to="/terms" className="text-slate-500 hover:text-[#0EA5E9] transition-colors">Terms</Link>
            <span className="text-slate-300">•</span>
            <Link to="/privacy" className="text-slate-500 hover:text-[#0EA5E9] transition-colors">Privacy</Link>
            <span className="text-slate-300">•</span>
            <Link to="/refunds" className="text-slate-500 hover:text-[#0EA5E9] transition-colors">Refunds</Link>
            <span className="text-slate-300">•</span>
            <Link to="/beta" className="text-slate-500 hover:text-[#0EA5E9] transition-colors">Beta Program</Link>
          </div>
        </div>
      </div>

      <BottomTabNav />
    </div>
  );
};

export default HomePage;
